import React, { useCallback, useContext, useMemo } from 'react'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getDepositAmountPercentage, isDefaultDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOption, getCheapestAvailablePackageOption, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import GeoContext from 'contexts/geoContext'
import ReserveForZeroPolicyModal from '../ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import ReserveForZeroAndDepositPolicyModal from '../ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import ModalContext from 'contexts/ModalContext'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import moment from 'moment'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  display: flex;
  padding: ${rem(16)};
`

const RightSide = styled.div`
  margin-left: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: baseline;
  }
`

const StyledLink = styled(TextLink)`
  ${mediaQueryUp.desktop} {
    margin-left: ${rem(8)};
  }
`

interface Props {
  packageView: OfferPackageOptionView
  offer: App.Offer,
  checkIn: string,
  defaultDepositAmountPercentage: number;
}

function ReserveForZeroPaymentBanner({ offer, checkIn, packageView, defaultDepositAmountPercentage }: Props) {
  const showModal = useContext(ModalContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentCurrency } = useContext(GeoContext)
  const { hotelTotal } = packageView

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelTotal)

  const isOfferUsingDefaultDepositPercentage = isDefaultDepositAmountPercentage(offer)

  const onFindOutMore = useCallback(async() => {
    if (depositsAvailable) {
      showModal(
        <ReserveForZeroAndDepositPolicyModal
          depositAmountPercentage={depositAmountPercentage}
          offerType={offer.type}
          depositThresholds={offer.depositThresholds}
          depositAmount={offer.depositAmount}
        />,
      )
    } else {
      showModal(
        <ReserveForZeroPolicyModal />,
      )
    }
  }, [depositsAvailable, showModal, depositAmountPercentage, offer])

  return (
    <Container>
      <LineInfoCircleIcon colour="primary" />
      <RightSide>
        {depositsAvailable && (
          <BodyText variant="medium">
            <BodyText as="span" variant="medium" weight="bold">Flexible payment options:&nbsp;</BodyText>
            Reserve your booking for free or with a {!isOfferUsingDefaultDepositPercentage && `${depositAmountPercentage}%`} deposit
          </BodyText>
        )}
        {!depositsAvailable && (
          <BodyText variant="medium">
            <BodyText as="span" variant="medium" weight="bold">Reserve for free:&nbsp;</BodyText>
            We'll hold your reservation for 48 hours so you have time to think it over.
          </BodyText>
        )}
        <StyledLink onClick={onFindOutMore} size="medium">Learn more</StyledLink>
      </RightSide>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageView: getCheapestSelectedPackageOption(appState, state) ?? getCheapestAvailablePackageOption(state, appState),
  offer: state.offer,
  checkIn: state.checkInDate,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}), ReserveForZeroPaymentBanner)
